import cx from 'classnames';
import { JSX, ReactNode, useMemo } from 'react';
import { Link as ReactRouterLink, LinkProps as ReactRouterLinkProps } from 'react-router-dom';
import styles from './Link.module.css';

export type InternalLinkProps = {
  'children': ReactNode;
  'onClick'?: VoidFunction;
  'to'?: string;
  'leftIcon'?: JSX.Element;
  'rightIcon'?: JSX.Element;
  'appearance'?: 'primary' | 'secondary' | 'secondary-inverse' | 'ghost' | 'tertiary';
  'size'?: 'L' | 'M' | 'S';
  'disabled'?: boolean;
  'className'?: string;
  'data-testid'?: string;
  'linkProps'?: Partial<ReactRouterLinkProps>;
  'isExternal'?: boolean;
  'hideTextDecoration'?: boolean;
};

export default function InternalLink({
  onClick,
  to,
  leftIcon,
  rightIcon,
  appearance = 'primary',
  size = 'S',
  'data-testid': dataTestId = 'link',
  disabled = false,
  className,
  children,
  linkProps = {},
  isExternal = false,
  hideTextDecoration = false,
}: InternalLinkProps) {
  const icon = leftIcon ?? rightIcon;

  const content = (
    <>
      <span className={styles.text}>{children}</span>
      {icon && <span className={styles.icon}>{icon}</span>}
    </>
  );

  const iconPosition = useMemo(() => {
    if (leftIcon) return 'left';
    if (rightIcon) return 'right';
    return false;
  }, [rightIcon, leftIcon]);

  const sharedProps = useMemo(
    () => ({
      'onClick': disabled ? undefined : onClick,
      'aria-disabled': disabled || undefined,
      'data-icon-position': iconPosition,
      'data-appearance': appearance,
      'data-size': size,
      'data-is-disabled': disabled,
      'data-testid': dataTestId,
      'data-hide-text-decoration': hideTextDecoration,
      'className': cx(styles.link, className),
    }),
    [onClick, iconPosition, appearance, size, disabled, dataTestId, className, hideTextDecoration],
  );

  if (disabled) {
    return <div {...sharedProps}>{content}</div>;
  }

  if (to) {
    if (to.startsWith('mailto:') || isExternal) {
      const externalProps = isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {};

      return (
        <a href={to} {...sharedProps} {...externalProps}>
          {content}
        </a>
      );
    }

    return (
      <ReactRouterLink to={to} {...sharedProps} {...linkProps}>
        {content}
      </ReactRouterLink>
    );
  }

  return <div {...sharedProps}>{content}</div>;
}
